/**
 * Native-platform detection for the Capacitor iOS/Android wrappers.
 *
 * Phase 1 of the iOS migration ships a Capacitor shell that points at the
 * existing React PWA without RevenueCat/IAP. To stay Apple-compliant
 * (Guideline 3.1.1 — IAP required for digital subscriptions), any UI that
 * launches a Stripe checkout MUST be hidden when the app runs natively.
 * Users on native iOS see the free experience and a notice that
 * subscriptions are managed on the web.
 *
 * Phase 2 will swap these guards for native RevenueCat purchase buttons.
 */

let _platform = null;

/** Lazily reads `window.Capacitor` (it's injected by the runtime). */
function _detect() {
  if (_platform !== null) return _platform;
  if (typeof window === "undefined") {
    _platform = { isNative: false, platform: "web" };
    return _platform;
  }
  const cap = window.Capacitor;
  const isNative = !!cap?.isNativePlatform?.();
  const platform = cap?.getPlatform?.() || "web";
  _platform = { isNative, platform };
  return _platform;
}

/** True when running inside a Capacitor-built iOS or Android app. */
export function isNative() {
  return _detect().isNative;
}

/** True only when running inside the native iOS wrapper. */
export function isNativeIOS() {
  const p = _detect();
  return p.isNative && p.platform === "ios";
}

/** "ios" | "android" | "web" */
export function getPlatform() {
  return _detect().platform;
}

/**
 * Apple Guideline 3.1.1 — IAP required for digital subscriptions.
 * Phase 1 hides all Stripe purchase entry points on native; Phase 2 will
 * replace these with RevenueCat-backed native purchase buttons.
 */
export function canShowWebCheckout() {
  return !isNative();
}
